import { useCallback, useEffect, useRef, useState } from 'react';
import {
  NamiCampaignManager,
  NamiFlowManager,
  NamiPaywallEvent,
  NamiPaywallLaunchContext,
  NamiPaywallManager,
} from '@namiml/web-sdk';
import ErrorMessage from '../components/ErrorMessage';
import LaunchContext from '../components/LaunchContext';
import { productDetails } from '../data/product-details';
import {
  NAMI_PAYWALL_CONTEXT,
  NAMI_SHOW_PAYWALL,
} from '../utils/const';
import { useNavigation } from '../navigation/NavigationProvider';

const APP_SUPPLIED_VIDEO_URL =
  'https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
const APP_SUPPLIED_VIDEO_NAME = 'app-supplied-video';

const PaywallPage = () => {
  const navigation = useNavigation();
  const label = navigation.query.get('label');
  const url = navigation.query.get('url');
  const requestedShow = navigation.query.get('showPaywall') === 'true';

  const paywallContainerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState('');
  const [showPaywall, setShowPaywall] = useState(requestedShow);
  const [context, setContext] = useState<NamiPaywallLaunchContext | undefined>();
  const [videoDelay, setVideoDelay] = useState<number>(0);

  const saveShowPaywall = useCallback((value: boolean) => {
    setShowPaywall(value);
    localStorage.setItem(NAMI_SHOW_PAYWALL, String(value));
  }, []);

  useEffect(() => {
    setShowPaywall(requestedShow || localStorage.getItem(NAMI_SHOW_PAYWALL) === 'true');
  }, [requestedShow]);

  const launchPaywallWithContext = useCallback(
    (launchContext: NamiPaywallLaunchContext, appVideoDelay: number) => {
      saveShowPaywall(true);
      setContext(launchContext);
      setVideoDelay(appVideoDelay);
    },
    [saveShowPaywall]
  );

  const triggerLaunch = useCallback(() => {
    if (!showPaywall) return;
    try {
      if (videoDelay > 0) {
        setTimeout(() => {
          NamiPaywallManager.setAppSuppliedVideoDetails(
            APP_SUPPLIED_VIDEO_URL,
            APP_SUPPLIED_VIDEO_NAME
          );
        }, videoDelay * 1000);
      }

      const resolvedContext =
        context ??
        (() => {
          const stored = localStorage.getItem(NAMI_PAYWALL_CONTEXT);
          return stored ? (JSON.parse(stored) as NamiPaywallLaunchContext) : undefined;
        })();

      const resultHandler = (success: boolean, err?: unknown) => {
        if (!success) {
          if (err) {
            console.error('Launch result handler error', err);
            setError(`Launch failed: ${String(err)}`);
          }
          localStorage.removeItem(NAMI_SHOW_PAYWALL);
          navigation.navigate('/');
        }
      };

      const actionHandler = (event: NamiPaywallEvent) => {
        console.log('Paywall event:', event);
      };

      const paywallComponent = NamiCampaignManager.launch(
        label ?? undefined,
        url ?? undefined,
        resolvedContext,
        resultHandler,
        actionHandler
      );

      NamiFlowManager.registerEventHandler((event: unknown) => {
        console.log('Flow event handler data', event);
      });

      NamiFlowManager.registerStepHandoff((handoffTag: string, handoffData?: unknown) => {
        console.log('Step handoff', handoffTag, handoffData);
        if (handoffTag === 'complete' && (handoffData as any)?.onboardingComplete) {
          navigation.navigate('/');
          return;
        }
        setTimeout(() => NamiFlowManager.resume(), 1000);
      });

      NamiPaywallManager.registerBuySkuHandler((sku) => {
        console.log('Buy SKU Handler triggered with handoff data:', {
          skuId: sku.id,
          skuName: sku.name,
          productId: sku.skuId,
          type: sku.type,
          promoId: sku.promoId,
          promoToken: sku.promoToken,
          productDetails: sku.productDetails,
          appleProduct: sku.appleProduct,
          googleProduct: sku.googleProduct,
          amazonProduct: sku.amazonProduct
        });
        console.log('Full SKU object:', sku);
      });

      const unregisterCloseHandler = NamiPaywallManager.registerCloseHandler(() => {
        unregisterCloseHandler();
        navigation.navigate('/');
      });

      if (paywallComponent && paywallContainerRef.current) {
        paywallContainerRef.current.innerHTML = '';
        paywallContainerRef.current.appendChild(paywallComponent);
      }
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Error rendering paywall: ${details}`);
      saveShowPaywall(false);
    }
  }, [context, label, navigation, saveShowPaywall, showPaywall, url, videoDelay]);

  useEffect(() => {
    NamiPaywallManager.setProductDetails(productDetails);
  }, []);

  useEffect(() => {
    triggerLaunch();
  }, [triggerLaunch]);

  return (
    <>
      {error && <ErrorMessage message={error} />}
      {showPaywall ? (
        <div ref={paywallContainerRef} />
      ) : (
        <LaunchContext
          label={label}
          url={url}
          launchHandler={launchPaywallWithContext}
        />
      )}
    </>
  );
};

export default PaywallPage;
